import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Target, Pencil, Trash2, Calendar } from 'lucide-react';
import { differenceInDays } from 'date-fns';

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-blue-100 text-blue-700',
  paused: 'bg-amber-100 text-amber-700',
};

export default function GoalCard({ goal, onEdit, onDelete, onUpdateProgress }) {
  const [editingProgress, setEditingProgress] = useState(false);
  const [newAmount, setNewAmount] = useState(String(goal.current_amount || 0));

  const progress = goal.target_amount > 0
    ? Math.min(100, ((goal.current_amount || 0) / goal.target_amount) * 100)
    : 0;
  const daysLeft = goal.deadline ? differenceInDays(new Date(goal.deadline), new Date()) : null;
  const remaining = Math.max(0, (goal.target_amount || 0) - (goal.current_amount || 0));

  const handleSave = () => {
    onUpdateProgress(parseFloat(newAmount) || 0);
    setEditingProgress(false);
  };

  return (
    <Card className="border-0 shadow-sm bg-white">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-emerald-50 rounded-lg flex-shrink-0">
              <Target className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">{goal.name}</h3>
              {goal.description && <p className="text-xs text-slate-400 mt-0.5">{goal.description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Badge className={`${STATUS_COLORS[goal.status] || STATUS_COLORS.active} text-xs`}>
              {goal.status}
            </Badge>
            <Button variant="ghost" size="icon" className="w-7 h-7" onClick={onEdit}>
              <Pencil className="w-3.5 h-3.5 text-slate-400" />
            </Button>
            <Button variant="ghost" size="icon" className="w-7 h-7" onClick={() => onDelete(goal.id)}>
              <Trash2 className="w-3.5 h-3.5 text-rose-400" />
            </Button>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">${(goal.current_amount || 0).toLocaleString('en-AU')} saved</span>
            <span className="font-medium text-slate-700">${(goal.target_amount || 0).toLocaleString('en-AU')} goal</span>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">{progress.toFixed(0)}% complete</span>
            {daysLeft !== null && (
              <span className={`flex items-center gap-1 ${daysLeft < 0 ? 'text-rose-500' : daysLeft < 30 ? 'text-amber-600' : 'text-slate-400'}`}>
                <Calendar className="w-3 h-3" />
                {daysLeft < 0 ? 'Overdue' : `${daysLeft} days left`}
              </span>
            )}
          </div>
          {remaining > 0 && (
            <p className="text-xs text-slate-400">${remaining.toLocaleString('en-AU')} remaining</p>
          )}
        </div>

        {editingProgress ? (
          <div className="flex gap-2">
            <Input
              type="number"
              value={newAmount}
              onChange={e => setNewAmount(e.target.value)}
              className="h-8 text-sm"
              placeholder="Amount saved so far"
            />
            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-xs px-3" onClick={handleSave}>Save</Button>
            <Button size="sm" variant="outline" className="h-8 text-xs px-3" onClick={() => setEditingProgress(false)}>Cancel</Button>
          </div>
        ) : (
          <Button
            variant="outline" size="sm" className="w-full h-8 text-xs"
            onClick={() => { setNewAmount(String(goal.current_amount || 0)); setEditingProgress(true); }}
          >
            Update Progress
          </Button>
        )}
      </CardContent>
    </Card>
  );
}